/**
 * Menu Component - Controller
 * ============================
 *
 * Controlador del componente Menu.
 */

import type { IScope, IAugmentedJQuery } from 'angular';
import {
  VARIANTS,
  SIZES,
  ICONS,
  BADGE_VARIANTS,
  DEFAULTS,
  MenuVariant,
  MenuSize,
  MenuOrientation,
  MenuItem,
  VariantStyles,
  SizeStyles
} from './constants';

// ============================================================================
// TYPES
// ============================================================================

export interface MenuBindings {
  items?: MenuItem[] | string;
  variant?: MenuVariant;
  size?: MenuSize;
  orientation?: MenuOrientation;
  collapsible?: boolean;
  showIcons?: boolean;
  activeItemId?: string;
  onItemClick?: (args: { $event: Event; item: MenuItem }) => void;
  onItemHover?: (args: { $event: Event; item: MenuItem }) => void;
}

interface BindingChanges {
  items?: { currentValue: MenuItem[]; isFirstChange: () => boolean };
  activeItemId?: { currentValue: string; isFirstChange: () => boolean };
  [key: string]: unknown;
}

// ============================================================================
// CONTROLLER
// ============================================================================

export class MenuController implements MenuBindings {
  static $inject = ['$scope', '$element', '$sce'];

  private $scope: IScope | null;
  private $element: IAugmentedJQuery | null;
  private $sce: angular.ISCEService | null;

  // Bindings
  items?: MenuItem[] | string;
  variant?: MenuVariant;
  size?: MenuSize;
  orientation?: MenuOrientation;
  collapsible?: boolean;
  showIcons?: boolean;
  activeItemId?: string;
  onItemClick?: (args: { $event: Event; item: MenuItem }) => void;
  onItemHover?: (args: { $event: Event; item: MenuItem }) => void;

  // Parsed items
  parsedItems: MenuItem[] = [];

  // Estado interno
  expandedItems: Set<string> = new Set();
  trustedIcons: Map<string, unknown> = new Map();

  // Estado compound: items registrados vía ecMenuItem (patrón de registro)
  compoundItems: MenuItemController[] = [];

  constructor(
    $scope: IScope,
    $element: IAugmentedJQuery,
    $sce: angular.ISCEService
  ) {
    this.$scope = $scope;
    this.$element = $element;
    this.$sce = $sce;

    this.handleItemClick = this.handleItemClick.bind(this);
    this.handleItemHover = this.handleItemHover.bind(this);
    this.toggleSubmenu = this.toggleSubmenu.bind(this);
  }

  // =========================================================================
  // LIFECYCLE HOOKS
  // =========================================================================

  $onInit(): void {
    this._applyDefaults();
    this._parseItems();
    this._prepareIcons();
  }

  $onChanges(changes: BindingChanges): void {
    if (changes.items && !changes.items.isFirstChange()) {
      this._parseItems();
      this._prepareIcons();
    }
  }

  $onDestroy(): void {
    this.$scope = null;
    this.$element = null;
    this.$sce = null;
    this.expandedItems.clear();
    this.trustedIcons.clear();
  }

  // =========================================================================
  // MÉTODOS PRIVADOS
  // =========================================================================

  private _applyDefaults(): void {
    this.variant = this.variant || DEFAULTS.variant;
    this.size = this.size || DEFAULTS.size;
    this.orientation = this.orientation || DEFAULTS.orientation;
    this.collapsible = this.collapsible || DEFAULTS.collapsible;
    this.showIcons = this.showIcons !== false;
    this.items = this.items || [];
  }

  private _parseItems(): void {
    if (typeof this.items === 'string') {
      try {
        this.parsedItems = JSON.parse(this.items);
      } catch (e) {
        console.error('Error parsing menu items:', e);
        this.parsedItems = [];
      }
    } else if (Array.isArray(this.items)) {
      this.parsedItems = this.items;
    } else {
      this.parsedItems = [];
    }
  }

  private _prepareIcons(): void {
    if (!this.$sce || !this.parsedItems) return;

    const prepareItemIcons = (items: MenuItem[]): void => {
      items.forEach(item => {
        if (item.icon && ICONS[item.icon]) {
          this.trustedIcons.set(item.id, this.$sce!.trustAsHtml(ICONS[item.icon]));
        }
        if (item.children) {
          prepareItemIcons(item.children);
        }
      });
    };

    prepareItemIcons(this.parsedItems);

    // Prepare chevron icons
    this.trustedIcons.set('chevron-right', this.$sce.trustAsHtml(ICONS['chevron-right']));
    this.trustedIcons.set('chevron-down', this.$sce.trustAsHtml(ICONS['chevron-down']));
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - CLASES CSS
  // =========================================================================

  getContainerClasses(): string[] {
    const variantStyles = this.getVariantClasses();
    const sizeStyles = this.getSizeClasses();

    const classes = [
      variantStyles.container,
      sizeStyles.container
    ];

    if (this.orientation === 'horizontal') {
      classes.push('ec-flex', 'ec-items-center', sizeStyles.gap);
    } else {
      classes.push('ec-flex', 'ec-flex-col');
    }

    return classes;
  }

  getVariantClasses(): VariantStyles {
    return VARIANTS[this.variant || DEFAULTS.variant] || VARIANTS[DEFAULTS.variant];
  }

  getSizeClasses(): SizeStyles {
    return SIZES[this.size || DEFAULTS.size] || SIZES[DEFAULTS.size];
  }

  getItemClasses(item: MenuItem): string[] {
    const variantStyles = this.getVariantClasses();
    const sizeStyles = this.getSizeClasses();

    const classes = [
      'ec-flex ec-items-center ec-cursor-pointer ec-transition-all ec-duration-150',
      sizeStyles.item,
      sizeStyles.gap,
      variantStyles.item
    ];

    if (item.disabled) {
      classes.push('ec-opacity-50 ec-cursor-not-allowed');
    } else {
      classes.push(variantStyles.itemHover);
    }

    if (this.isItemActive(item)) {
      classes.push(variantStyles.itemActive);
    }

    return classes;
  }

  getIconClasses(): string[] {
    const variantStyles = this.getVariantClasses();
    const sizeStyles = this.getSizeClasses();

    return [
      'ec-flex-shrink-0',
      sizeStyles.icon,
      variantStyles.icon
    ];
  }

  getLabelClasses(): string[] {
    const variantStyles = this.getVariantClasses();
    const sizeStyles = this.getSizeClasses();

    return [
      'ec-flex-1',
      sizeStyles.label,
      variantStyles.label
    ];
  }

  getBadgeClasses(item: MenuItem): string[] {
    const sizeStyles = this.getSizeClasses();
    const badgeVariant = item.badgeVariant || 'default';

    return [
      'ec-rounded-full ec-font-medium',
      sizeStyles.badge,
      BADGE_VARIANTS[badgeVariant] || BADGE_VARIANTS.default
    ];
  }

  getDividerClasses(): string[] {
    const variantStyles = this.getVariantClasses();

    if (this.orientation === 'horizontal') {
      return ['ec-w-px ec-h-6 ec-mx-2', variantStyles.divider, 'ec-border-l'];
    }
    return ['ec-w-full ec-my-1', variantStyles.divider, 'ec-border-t'];
  }

  getSubmenuClasses(): string[] {
    const variantStyles = this.getVariantClasses();

    return [
      'ec-ml-4 ec-mt-1 ec-pl-4 ec-border-l-2',
      variantStyles.divider
    ];
  }

  getChevronClasses(item: MenuItem): string[] {
    const sizeStyles = this.getSizeClasses();

    const classes = [
      'ec-flex-shrink-0 ec-transition-transform ec-duration-200',
      sizeStyles.icon
    ];

    if (this.isExpanded(item)) {
      classes.push('ec-rotate-90');
    }

    return classes;
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - ESTADO
  // =========================================================================

  isItemActive(item: MenuItem): boolean {
    return item.active === true || item.id === this.activeItemId;
  }

  isExpanded(item: MenuItem): boolean {
    return this.expandedItems.has(item.id);
  }

  hasChildren(item: MenuItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  getIcon(iconKey: string): unknown {
    return this.trustedIcons.get(iconKey) || '';
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - EVENTOS
  // =========================================================================

  handleItemClick($event: Event, item: MenuItem): void {
    if (item.disabled) {
      $event.preventDefault();
      $event.stopPropagation();
      return;
    }

    if (this.hasChildren(item)) {
      this.toggleSubmenu(item);
    }

    if (typeof this.onItemClick === 'function') {
      this.onItemClick({ $event, item });
    }
  }

  handleItemHover($event: Event, item: MenuItem): void {
    if (item.disabled) return;

    if (typeof this.onItemHover === 'function') {
      this.onItemHover({ $event, item });
    }
  }

  toggleSubmenu(item: MenuItem): void {
    if (this.expandedItems.has(item.id)) {
      this.expandedItems.delete(item.id);
    } else {
      this.expandedItems.add(item.id);
    }
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - COMPOUND (ecMenuItem)
  // =========================================================================

  /**
   * Registrado por un hijo `ecMenuItem` en su `$onInit` (con guard: solo si
   * hay padre real — ver `MenuItemController`).
   */
  registerItem(item: MenuItemController): void {
    this.compoundItems.push(item);
  }

  /**
   * Deregistrado por un hijo `ecMenuItem` en su `$onDestroy` (mismo guard).
   */
  deregisterItem(item: MenuItemController): void {
    const index = this.compoundItems.indexOf(item);
    if (index > -1) {
      this.compoundItems.splice(index, 1);
    }
  }

  /**
   * Invocado por un hijo `ecMenuItem` al hacer click. Reusa el mismo contrato
   * `onItemClick` del modo data-driven.
   */
  notifyItemClick(id: string, $event: Event): void {
    if (typeof this.onItemClick === 'function') {
      // Cast seguro: el contrato onItemClick solo exige un objeto con forma
      // MenuItem para uso del consumidor; el hijo compound no conoce (ni
      // necesita) label/icon/etc., solo el id que él mismo declaró.
      this.onItemClick({ $event, item: { id } as MenuItem });
    }
  }

  /**
   * Modo compound: sin binding `items` (o vacío) y con al menos un
   * `ecMenuItem` registrado. Es un GETTER (no se snapshotea en `$onInit`)
   * porque el registro de los hijos ocurre en su link, que corre DESPUÉS
   * del `$onInit` del padre — solo un getter evaluado en cada digest ve el
   * valor correcto.
   */
  get isCompound(): boolean {
    return (!this.items || this.items.length === 0) && this.compoundItems.length > 0;
  }
}

export default MenuController;

// ============================================================================
// CONTROLLER DEL HIJO COMPOUND (ecMenuItem)
// ============================================================================

/**
 * Hijo compound: item de menú que se auto-registra en el padre `ecMenu`.
 * Renderiza su propio `<li>` reutilizando (vía delegación) las clases del
 * item data-driven, para paridad pixel entre ambos modos.
 */
export class MenuItemController {
  static $inject = ['$sce'];

  // require ^?ecMenu (opcional + guard): ver "Mecánica común" del patrón
  // compound — el require SIEMPRE debe ser opcional para hijos con estado
  // del padre, disponible desde $onInit. Nunca $scope.$parent ni eventos.
  menu?: MenuController;

  // Bindings
  itemId?: string;
  icon?: string;
  badge?: string;
  disabled?: boolean;
  onClick?: (args: { $event: Event }) => void;

  // Ícono confiado para ng-bind-html
  trustedIcon: unknown = '';

  private $sce: angular.ISCEService | null;

  constructor($sce: angular.ISCEService) {
    this.$sce = $sce;
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  $onInit(): void {
    if (this.icon && this.$sce) {
      this.trustedIcon = this.$sce.trustAsHtml(this.icon);
    }

    if (this.menu) {
      this.menu.registerItem(this);
    }
  }

  $onDestroy(): void {
    if (this.menu) {
      this.menu.deregisterItem(this);
    }
    this.$sce = null;
  }

  handleClick($event: Event): void {
    if (this.disabled) {
      return;
    }

    if (typeof this.onClick === 'function') {
      this.onClick({ $event });
    }

    if (this.menu && this.itemId !== undefined) {
      this.menu.notifyItemClick(this.itemId, $event);
    }
  }

  /**
   * Teclado (Enter / Espacio → click). Mirror de CardController.handleKeydown
   * para paridad de a11y entre compound components.
   */
  handleKeydown($event: KeyboardEvent): void {
    if (this.disabled) return;

    if ($event.key === 'Enter' || $event.key === ' ') {
      $event.preventDefault();
      this.handleClick($event);
    }
  }

  // =========================================================================
  // CLASES CSS — delegadas al padre para paridad pixel con items data-driven
  // =========================================================================

  getClasses(): string[] {
    if (!this.menu) return [];
    // Cast seguro: getItemClasses solo lee `disabled` y `id`/`active` (vía
    // isItemActive) del item recibido; el hijo compound no tiene label/icon
    // como MenuItem completo, pero eso no afecta el cálculo de clases.
    return this.menu.getItemClasses({ id: this.itemId, disabled: this.disabled } as MenuItem);
  }

  getIconClasses(): string[] {
    return this.menu ? this.menu.getIconClasses() : [];
  }

  getLabelClasses(): string[] {
    return this.menu ? this.menu.getLabelClasses() : [];
  }

  getBadgeClasses(): string[] {
    if (!this.menu) return [];
    // Cast seguro: getBadgeClasses solo lee `badgeVariant` (ausente acá, cae
    // al default) e ignora el resto de MenuItem.
    return this.menu.getBadgeClasses({ id: this.itemId, badge: this.badge } as MenuItem);
  }
}
